import { ListingInfo, NFT1155ListingInfo } from '@apeiron/common'
import { NextImage } from '@apeiron/temp'
import { MuiCheckbox } from '@apeiron/ui'
import { Box, InputAdornment, styled, Typography } from '@mui/material'
import InfoSVG from '@public/icons/icon-info.svg'
import ApeironConfirmButton from '@src/components/share/apeiron/ApeironConfirmButton'
import PriceInputSection from '@src/components/share/trade/sellDialogView/TradeSellPriceInputView'
import TradeQuanatitySection from '@src/components/share/trade/TradeQuantitySection'
import { FeatureFlagType } from '@src/constants/featureFlag'
import { INPUT_PROPS } from '@src/constants/layout'
import { SaleMethodType, SKIP_QUANTITY } from '@src/constants/trade'
import { AuctionSaleMethod, TradeParty } from '@src/types/trade'
import featureAllowed from '@src/util/apeiron/featureAllowed'
import * as R from 'ramda'
import {
  ChangeEvent,
  FC,
  FormEvent,
  useCallback,
  useMemo,
  useState
} from 'react'
import { Trans, useTranslation } from 'react-i18next'

const Form = styled('form')`
  display: flex;
  flex-direction: column;
  gap: 16px;
  ${props => props.theme.breakpoints.up('res480')} {
    padding: 0 44px;
  }
  ${props => props.theme.breakpoints.down('res480')} {
    padding: 0px;
  }
`

const DescriptionText = styled(Typography)`
  color: #838383;
  font-size: 16px;
  font-weight: 300;
  line-height: 20px;
  margin: 0px -2px;
`

const StyleCheckBox = styled(MuiCheckbox)`
  padding: 0;
`

const CheckBoxContainer = styled(Box)`
  padding: 0 46px;
  margin-bottom: 24px;
  display: flex;
  justify-items: center;
  align-items: center;
`

const CheckBoxDescription = styled(Box)`
  margin-left: 12px;
  font-size: 16px;
  line-height: 18px;
  color: #b1b3b3;
`

const StyledConfirmButton = styled(ApeironConfirmButton)`
  margin-top: 20px;
  margin-left: auto;
`

const InfoSection = styled(Box)`
  align-items: flex-start;
  display: flex;
  gap: 7px;
  justify-content: flex-end;
`

const InfoTextContainer = styled(Box)`
  display: flex;
  flex-direction: column;
  gap: 5px;
`

const InfoText = styled(Typography)`
  font-size: 14px;
  font-weight: 400;
  line-height: 18px;
  text-align: right;
`

const HighlightInfoText = styled(InfoText)`
  color: #ffbf36;
  display: inline;
`

const SmallInfoText = styled(InfoText)`
  font-size: 12px;
  font-weight: 300;
  line-height: 16px;
`

const HighlightSmallInfoText = styled(SmallInfoText)`
  color: #ffbf36;
  display: inline;
`

const StyledInputAdornment = styled(InputAdornment)`
  p {
    color: #808080;
  }
`

const StyledIconInfo = styled(NextImage)`
  aspect-ratio: 1;
  height: 16px;
  margin-top: 1px;
`

const AuctionView: FC<Props> = props => {
  const {
    checkboxRemark,
    className,
    isEditMode,
    listingInfo,
    loading,
    totalQuantity,
    tradeParties,
    onConfirm
  } = props

  const { t } = useTranslation()

  const values = useMemo((): DefaultValues => {
    const duration = R.propOr(0, 'duration', listingInfo) as number

    return {
      duration,
      endPrice: duration > 0 ? R.propOr(0, 'endPrice', listingInfo) : 0,
      quantity: duration > 0 ? R.propOr(1, 'quantity', listingInfo) : 1,
      startPrice: duration > 0 ? R.propOr(0, 'startPrice', listingInfo) : 0
    }
  }, [listingInfo])

  const feePercentage = R.sum(R.pluck('fee', tradeParties))

  const [auctionData, setAuctionData] = useState<AuctionSaleMethod>({
    type: SaleMethodType.Auction,
    startPrice: values.startPrice,
    endPrice: values.endPrice,
    duration: values.duration
  })

  const [quantity, setQuantity] = useState<number>(values.quantity)

  const [isChecked, setIsChecked] = useState(
    !featureAllowed(FeatureFlagType.ShowTradeRemark)
  )

  const isInputEmpty =
    auctionData.startPrice <= 0 ||
    auctionData.endPrice <= 0 ||
    auctionData.duration <= 0

  const isEnoughQuantity =
    totalQuantity === SKIP_QUANTITY ||
    (quantity > 0 && quantity <= totalQuantity)

  const onCheckBoxChange = useCallback(() => {
    setIsChecked(prev => !prev)
  }, [])

  const handleOnQuantityChange = useCallback((value: number) => {
    setQuantity(value)
  }, [])

  const onInputChange = useCallback((e: ChangeEvent<HTMLInputElement>) => {
    const { name, value } = e.currentTarget

    setAuctionData(prev => ({ ...prev, [name]: Number(value) || 0 }))
  }, [])

  const onDurationChange = useCallback((e: ChangeEvent<HTMLInputElement>) => {
    const { value } = e.currentTarget

    let integer = value

    if (value?.includes('.')) {
      integer = value.split('.')[0]
    }

    setAuctionData(prev => ({ ...prev, duration: Number(integer) || 0 }))
  }, [])

  const onFormSubmit = useCallback(
    (e: FormEvent<HTMLFormElement>) => {
      e.preventDefault()

      // Blur the active element (text fields) to apply changes to filter
      const focusedElement = e.currentTarget.ownerDocument.activeElement

      if (focusedElement instanceof HTMLElement) {
        focusedElement.blur()
      }

      onConfirm?.(auctionData, quantity)
    },
    [auctionData, onConfirm, quantity]
  )

  return (
    <Box className={className}>
      <Form onSubmit={onFormSubmit}>
        <DescriptionText>
          {t('dialog.sell.auction.description')}
        </DescriptionText>
        {totalQuantity !== -1 && (
          <TradeQuanatitySection
            allowInput={!isEditMode}
            quantity={quantity}
            totalQuantity={totalQuantity}
            onChange={handleOnQuantityChange}
          />
        )}
        <PriceInputSection
          defaultValue={values.startPrice}
          autoFocus
          disabled={loading}
          inputProps={INPUT_PROPS.PLANET_SELLING_PRICE}
          label={t('dialog.sell.auction.input.label.start_price')}
          name='startPrice'
          onInputChange={onInputChange}
        />
        <PriceInputSection
          defaultValue={values.endPrice}
          disabled={loading}
          inputProps={INPUT_PROPS.PLANET_SELLING_PRICE}
          label={t('dialog.sell.auction.input.label.end_price')}
          name='endPrice'
          onInputChange={onInputChange}
        />
        <PriceInputSection
          defaultValue={values.duration}
          disabled={loading}
          endAdornment={
            <StyledInputAdornment position='end'>
              {t('dialog.sell.auction.input.unit.day', {
                count: auctionData.duration || 1
              })}
            </StyledInputAdornment>
          }
          inputProps={INPUT_PROPS.PLANET_SELLING_DURATION}
          label={t('dialog.sell.auction.input.label.duration')}
          name='duration'
          value={auctionData.duration}
          showIcon={false}
          onInputChange={onDurationChange}
        />

        <InfoSection>
          <StyledIconInfo src={InfoSVG} />
          <InfoTextContainer>
            <InfoText as='span'>
              <Trans
                i18nKey='dialog.sell.auction.info'
                components={{ highlight: <HighlightInfoText /> }}
                values={{ feePercentage }}
              />
            </InfoText>
            {R.map((tradeParty: TradeParty) => {
              return (
                <SmallInfoText key={tradeParty.type}>
                  <Trans
                    i18nKey={`dialog.sell.fee.${tradeParty.type}`}
                    components={{
                      highlight: <HighlightSmallInfoText as='span' />
                    }}
                    values={{ feePercentage: tradeParty.fee }}
                  />
                </SmallInfoText>
              )
            }, tradeParties)}
          </InfoTextContainer>
        </InfoSection>
        {featureAllowed(FeatureFlagType.ShowTradeRemark) &&
          !R.isNil(checkboxRemark) && (
            <CheckBoxContainer>
              <StyleCheckBox
                checked={isChecked}
                disabled={loading}
                onChange={onCheckBoxChange}
              />
              <CheckBoxDescription>{checkboxRemark}</CheckBoxDescription>
            </CheckBoxContainer>
          )}
        <StyledConfirmButton
          disabled={!isChecked || isInputEmpty || !isEnoughQuantity}
          loading={loading}
          type='submit'
        />
      </Form>
    </Box>
  )
}

type DefaultValues = {
  duration: number
  endPrice: number
  quantity: number
  startPrice: number
}

type Props = {
  checkboxRemark?: string
  className?: string
  isEditMode: boolean
  listingInfo?: ListingInfo | NFT1155ListingInfo | null
  loading?: boolean
  totalQuantity: number
  tradeParties: TradeParty[]
  onConfirm?: (saleMethod: AuctionSaleMethod, quantity: number) => void
}

export default AuctionView
